/**
 * Composer post type: create a Job post and share it to the feed.
 *
 * Mirrors classType in architecture: two-step backend create (initialize
 * then PUT with status='active'), then a `job_share` payload handed back
 * to the shell for the feed post. The Settings-side form
 * (src/components/CreateJobPost/) has more fields and a preview step that
 * the composer intentionally skips — the composer is the fast path.
 * Users who want to stage a draft or tweak less-common fields still use
 * the Settings flow.
 */

import {useEffect, useRef} from 'react'
import {createJobPost, initializeJobPost, setJobPostQuestions} from 'api'
import AddressAutocomplete from '../_shared/AddressAutocomplete'
import ProfessionPicker from '../_shared/ProfessionPicker'
import {
  CompactChips,
  Label,
  Section,
  Select,
  inputClass,
} from '../_shared/formKit'
import {addressFromBusiness} from '../_shared/businessAddress'
import JobQuestionsEditor from '../_shared/JobQuestionsEditor'
import {jobRoles, workTypes, compensationTypes} from 'constants/onboarding'

const MAX_BODY_LENGTH = 5000

const createInitialState = ({business} = {}) => {
  const seededAddress = addressFromBusiness(business)
  return {
    body: '',
    jobRole: '',
    description: '',
    // Display-string fallback. Mirrored from address.formatted on selection.
    location: seededAddress?.formatted || '',
    // Structured address from Google Places. Required for active publish;
    // defaults to the business address so most listings publish without
    // re-typing the location.
    address: seededAddress,
    employmentTypes: [],
    compensationType: '',
    minSalary: '',
    maxSalary: '',
    // Asked when someone applies: [{name, answerKind, required}], max 3.
    questions: [],
    openings: 1,
    responseWindowHours: 48,
  }
}

const RESPONSE_WINDOWS = [
  {value: 24, label: '1 day'},
  {value: 48, label: '2 days'},
  {value: 72, label: '3 days'},
  {value: 168, label: '1 week'},
]

const JobBody = ({state, setState, setError}) => {
  const descriptionRef = useRef(null)
  useEffect(() => {
    const t = setTimeout(() => descriptionRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [])

  const update = (field) => (eventOrValue) => {
    const value =
      eventOrValue && eventOrValue.target
        ? eventOrValue.target.value
        : eventOrValue
    setState((prev) => ({...prev, [field]: value}))
    setError('')
  }

  return (
    <div>
      <textarea
        value={state.body}
        onChange={update('body')}
        placeholder="Add an optional caption... (shown above the job card)"
        maxLength={MAX_BODY_LENGTH}
        rows={2}
        className="w-full resize-none border-0 outline-none text-sm text-sharpenn-black placeholder:text-green-gray bg-transparent focus:ring-0"
      />

      <Section step={1} title="Basics">
        <div>
          <Label>Role</Label>
          <ProfessionPicker
            options={jobRoles}
            value={state.jobRole ? [state.jobRole] : []}
            multiple={false}
            placeholder="Select a role…"
            onChange={(next) => {
              setState((prev) => ({...prev, jobRole: next[0] || ''}))
              setError('')
            }}
          />
        </div>

        <div>
          <Label>Description</Label>
          <textarea
            ref={descriptionRef}
            value={state.description}
            onChange={update('description')}
            placeholder="What does the role involve?"
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </div>
      </Section>

      <Section step={2} title="Employment type">
        <div>
          <Label>Employment types</Label>
          <CompactChips
            options={workTypes}
            selected={state.employmentTypes}
            multiple
            onChange={(next) => {
              setState((prev) => ({...prev, employmentTypes: next}))
              setError('')
            }}
          />
        </div>
      </Section>

      <Section step={3} title="Compensation">
        <div>
          <Label>Compensation</Label>
          <CompactChips
            options={compensationTypes}
            selected={state.compensationType ? [state.compensationType] : []}
            onChange={(next) => {
              setState((prev) => ({...prev, compensationType: next[0] || ''}))
              setError('')
            }}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label optional>Min salary</Label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={state.minSalary}
              onChange={update('minSalary')}
              placeholder="0"
              className={inputClass}
            />
          </div>
          <div>
            <Label optional>Max salary</Label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={state.maxSalary}
              onChange={update('maxSalary')}
              placeholder="0"
              className={inputClass}
            />
          </div>
        </div>
      </Section>

      <Section step={4} title="Location">
        <div>
          <Label>Address</Label>
          <AddressAutocomplete
            value={state.address}
            onChange={(addr) => {
              setState((prev) => ({
                ...prev,
                address: addr,
                location: addr?.formatted || '',
              }))
              setError('')
            }}
            placeholder="Start typing the address…"
            className={inputClass}
          />
        </div>
      </Section>

      <Section step={5} title="Application questions">
        <p className="text-xs text-content-tertiary -mt-2">
          Optional. Applicants answer these when they apply, in writing or on
          video, so you know what you need before moving forward.
        </p>
        <JobQuestionsEditor
          questions={state.questions}
          onChange={(questions) => {
            setState((prev) => ({...prev, questions}))
            setError('')
          }}
        />
      </Section>

      <Section step={6} title="Hiring">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Openings</Label>
            <input
              type="number"
              min="1"
              max="50"
              value={state.openings}
              onChange={update('openings')}
              className={inputClass}
            />
          </div>
          <div>
            <Label>Reply time</Label>
            <Select
              value={state.responseWindowHours}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  responseWindowHours: Number(e.target.value),
                }))
              }
            >
              {RESPONSE_WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <p className="text-xs text-content-tertiary">
          How long you and applicants each have to respond at every step.
          Listings stay up for 30 days and you can extend them.
        </p>
      </Section>
    </div>
  )
}

const canSubmit = (state) =>
  Boolean(
    state.jobRole &&
    state.description.trim() &&
    state.address?.placeId &&
    state.employmentTypes.length > 0 &&
    state.compensationType &&
    state.questions.every((q) => q.name.trim()) &&
    Number.isInteger(Number(state.openings)) &&
    Number(state.openings) >= 1 &&
    Number(state.openings) <= 50
  )

const submit = async ({state, authorCtx, setStatus}) => {
  if (!authorCtx.businessId) {
    throw new Error('Jobs must be posted as a business.')
  }

  setStatus('Creating job...')
  const initialized = await initializeJobPost(authorCtx.businessId)
  const jobId = initialized?.id
  if (!jobId) {
    throw new Error('Could not initialize the job post. Please try again.')
  }

  // Questions go on before publishing so nobody can apply without them.
  if (state.questions.length > 0) {
    setStatus('Adding questions...')
    await setJobPostQuestions({
      id: jobId,
      questions: state.questions.map((q) => ({
        name: q.name.trim(),
        answerKind: q.answerKind,
        required: q.required,
      })),
    })
  }

  await createJobPost({
    id: jobId,
    jobData: {
      // jobRole values are already snake_case in the constants file,
      // but the existing flow stores `title` as the role with
      // spaces→underscores, so we match.
      title: state.jobRole,
      description: state.description.trim(),
      // Display-string fallback. Structured `address` is the source of truth.
      location: state.address?.formatted || state.location.trim(),
      address: state.address,
      employmentTypes: state.employmentTypes,
      compensationType: state.compensationType,
      minSalary: state.minSalary ? Number(state.minSalary) : undefined,
      maxSalary: state.maxSalary ? Number(state.maxSalary) : undefined,
      openings: Number(state.openings),
      responseWindowHours: state.responseWindowHours,
      status: 'active',
      type: 'public',
      businessId: authorCtx.businessId,
    },
  })

  setStatus('Posting...')
  return {
    type: 'job_share',
    businessId: authorCtx.businessId,
    jobPostId: jobId,
    body: state.body.trim() || null,
  }
}

const jobType = {
  id: 'job',
  label: 'Job',
  icon: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  // Business-only, but offered to anyone with a business: the composer
  // asks which business to post as.
  canUse: ({actorKind, hasBusinesses}) =>
    actorKind === 'business' || Boolean(hasBusinesses),
  requiresBusiness: true,
  createInitialState,
  Body: JobBody,
  canSubmit,
  submit,
  submitLabel: 'Publish job',
}

export default jobType
